import React, { useContext } from "react";
import { NodeGroup } from "react-move";

import {
  ImageOptionExtended,
  OptionEnum,
  OptionModeEnum,
  OptionTypeEnum,
} from "@/components/Option";
import { GlobalContext } from "@/contexts";
import { Option, Vote, VotingSlide } from "@/service/Model";

import { ImageGroupExtended, NodeWrapperExtended } from "./styles";

export type ImageOptionsExtendedProps = {
  animationDelay: number;
  interactionId: string;
  isSecret?: boolean;
  onVote: (vote: Vote, isVoted: boolean) => void;
  overlayContent?: string;
  resolveClickable: (option: Option) => boolean;
  resolveOption: (option: Option) => OptionEnum | undefined;
  resolveOptionMode: (option: Option) => OptionModeEnum;
  resolveOverlayContent: (option: Option) => string | undefined;
  resolvePercent: (option: Option) => number | undefined;
  resolveCheckmark: (option: Option) => boolean;
  slide: VotingSlide;
  type: OptionTypeEnum;
};

export const ImageOptionsExtended: React.FC<ImageOptionsExtendedProps> = (
  props: ImageOptionsExtendedProps
): React.JSX.Element => {
  const { slide } = props;
  const globalContext = useContext(GlobalContext);
  const startPixel = -30;

  const reactMoveNodes = slide.options.map((option: Option, index: number) => {
    return {
      key: index,
      children: (
        <ImageOptionExtended
          answer={props.resolveOption(option)}
          description={option.description}
          icons={globalContext.icons}
          imageUrl={option.content}
          interactionId={props.interactionId}
          isClickable={props.resolveClickable(option)}
          key={index}
          mode={props.resolveOptionMode(option)}
          name={option.name}
          onClick={props.onVote}
          optionId={option.id}
          overlayContent={props.resolveOverlayContent(option)}
          percent={props.resolvePercent(option)}
          checkmark={props.resolveCheckmark(option)}
          slideId={props.slide.id}
          type={props.type}
        />
      ),
    };
  });

  return (
    <NodeGroup
      data={reactMoveNodes}
      keyAccessor={(d: any) => d.key}
      start={() => ({ opacity: 0, move: startPixel })}
      update={() => ({ opacity: [1], move: [0] })}
    >
      {(nodes: any) => (
        <NodeWrapperExtended>
          {nodes.map((node: any, index: number) => (
            <ImageGroupExtended
              key={node.key}
              $delay={props.animationDelay * (index + 1)}
              style={{
                transform: `translateX(${node.state.move}px)`,
                opacity: node.state.opacity,
              }}
            >
              {node.data.children}
            </ImageGroupExtended>
          ))}
        </NodeWrapperExtended>
      )}
    </NodeGroup>
  );
};
